<!--
  Travel Carbon Estimator
  Copyright (C) 2026  Manuel Leduc

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as published by
  the Free Software Foundation, either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see <http://www.gnu.org/licenses/>.
-->
<script setup lang="ts">
import { RouterLink, useRouter } from 'vue-router'
import AppLogo from '@/components/AppLogo.vue'
import { useThemeMode } from '@/composables/useThemeMode'
import { useAuth } from '@/composables/useAuth'
import { availableLocales, setLocale } from '@/composables/useLocale'
import {
  mdiAccountCircle,
  mdiLogin,
  mdiLogout,
  mdiTranslate,
  mdiWeatherNight,
  mdiWeatherSunny,
} from '@mdi/js'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const { mode, toggle } = useThemeMode()
const { isConfigured, isSignedIn, user, signOut } = useAuth()
const router = useRouter()

async function handleSignOut() {
  await signOut()
  router.push('/')
}
</script>

<template>
  <v-app>
    <v-app-bar color="primary" density="comfortable">
      <RouterLink to="/" custom>
        <template #default="{ href, navigate }">
          <a
            :href="href"
            class="d-flex align-center text-decoration-none ml-4"
            style="color: inherit"
            @click="navigate"
          >
            <AppLogo :size="32" />
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span class="ml-3 text-h6 font-weight-medium">Travel Carbon Estimator</span>
          </a>
        </template>
      </RouterLink>

      <v-spacer />

      <template v-if="isConfigured">
        <v-btn v-if="!isSignedIn" variant="text" :prepend-icon="mdiLogin" to="/sign-in">
          {{ t('common.signIn') }}
        </v-btn>
        <v-menu v-else>
          <template #activator="{ props }">
            <v-btn variant="text" :prepend-icon="mdiAccountCircle" v-bind="props">
              {{ user?.email ?? t('common.account') }}
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item :prepend-icon="mdiLogout" @click="handleSignOut">
              <v-list-item-title>{{ t('common.signOut') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <v-btn
        variant="text"
        :prepend-icon="mode === 'dark' ? mdiWeatherSunny : mdiWeatherNight"
        @click="toggle"
      >
        {{ mode === 'dark' ? t('common.lightMode') : t('common.darkMode') }}
      </v-btn>

      <v-menu>
        <template #activator="{ props: menuProps }">
          <v-btn :icon="mdiTranslate" variant="text" v-bind="menuProps" />
        </template>
        <v-list density="compact">
          <v-list-item
            v-for="locale in availableLocales"
            :key="locale.code"
            :title="locale.label"
            @click="setLocale(locale.code)"
          />
        </v-list>
      </v-menu>
    </v-app-bar>
    <v-main>
      <v-container>
        <RouterView />
      </v-container>
    </v-main>
  </v-app>
</template>
